import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';

import { useFetchBuildingTargetReportQuery } from 'store/apis';
import { selectCurrentUserInfo } from 'store/slices';
import { useAppSelector } from 'store/store.hook';

dayjs.extend(customParseFormat);

interface useTargetZoneReportProps {
  buildingId: number;
  start: string;
  end: string;
}

export const useTargetZoneReport = ({ buildingId, start, end }: useTargetZoneReportProps) => {
  const { orgName: organization, orgSlug } = useAppSelector(selectCurrentUserInfo);
  const {
    data: {
      building: { address = '', city = '', state = '', zip = '' } = {},
      summary: {
        categories = [],
        buildingAreas = [],
        completionScore: _completionScore = 0,
        actualNumberOfServices = 0,
        numberOfZones: totalZones = 0,
        target = 0,
      } = {},
      data: _floors = {},
    } = {},
    isFetching,
    ...rest
  } = useFetchBuildingTargetReportQuery({ orgSlug, id: buildingId, start, end }, { skip: !orgSlug });

  const categoryTargets = categories.map(
    ({
      category = '',
      numberOfZones: quantities = 0,
      target = 0,
      actualNumberOfServices = 0,
      completionScore = 0,
    } = {}) => ({
      category,
      quantities,
      target,
      actualNumberOfServices,
      completionScore,
    }),
  );

  const buildingAreaTargets = buildingAreas.map(
    ({
      area = '',
      numberOfZones: quantities = 0,
      target = 0,
      actualNumberOfServices = 0,
      completionScore = 0,
    } = {}) => ({
      area,
      quantities,
      target,
      actualNumberOfServices,
      completionScore,
    }),
  );

  const floors = Object.keys(_floors).map((floor) => {
    const categories = _floors[floor].map(
      ({
        category: area = '',
        completionScore = 0,
        zones: _zones = [],
        zonesNotServiced: zoneNotServicedCount = 0,
      } = {}) => {
        const zoneServiced = (_zones as any[]).map(
          ({
            zoneCode: code = '',
            zoneName: name = '',
            target = 0,
            actualNumberOfServices = 0,
            lastServicedBy = '',
            created = '',
          } = {}) => ({
            code,
            name,
            target,
            actualNumberOfServices,
            lastServicedBy,
            createdAt: created,
          }),
        );
        return { area, zoneServiced, zoneNotServicedCount, completionScore };
      },
    );

    return { floor, categories };
  });

  const building = { organization, address, city, state, zip };

  const serviceDate: [string, string] = [
    dayjs(new Date(start)).format('YYYY-MM-DD'),
    dayjs(new Date(end)).format('YYYY-MM-DD'),
  ];

  return {
    data: {
      building,
      serviceDate,
      statistics: {
        completionScore: Math.round(_completionScore * 100) / 100,
        target,
        actualNumberOfServices,
        totalZones,
      },
      categoryTargets,
      buildingAreaTargets,
      floors,
    },
    ...rest,
    isLoading: isFetching,
    isFetching,
  };
};
